import {getTestProps} from '@benchling/js-test-utils';
import {createStyles, Text, UnstyledButton} from '@mantine/core';
import React, {useCallback, useEffect} from 'react';

import {isAgentsFeatureEnabled} from 'llm/isAgentsFeatureEnabled';
import InlineBanner, {InlineBannerActionType, InlineBannerType} from 'ui-library/banners/InlineBanner';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {Container} from 'ui-library/NYC';

import {
  type AccumulatingChatHistory,
  CHAT_HISTORY_PAGE_SIZE,
  STARRED_CHAT_HISTORY_PAGE_SIZE,
  useAccumulatingChatHistory,
} from '../ChatListing';
import type {SidepanelChat} from '../ChatRouter';
import type {ChatSessionStarUpdate} from '../ChatSessionMenu';
import RedesignChatRow from './RedesignChatRow';
import SetUpAnAgentRow from './SetUpAnAgentRow';

const useStyles = createStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
    overflowY: 'auto',
    // Long chat titles ellipsis within the narrow sidebar; never scroll horizontally.
    overflowX: 'hidden',
  },
  // Minimized dropdown: flow at natural height (the dropdown itself scrolls).
  rootNatural: {
    height: 'auto',
    overflowY: 'visible',
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
  },
  items: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  refreshing: {
    opacity: 0.5,
  },
  sectionHeader: {
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: '#979799',
    margin: '18px 0 6px 4px',
  },
  viewMore: {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    padding: '0 4px',
    fontSize: 13,
    lineHeight: '20px',
    color: '#3C65FB',
    '&:hover': {
      textDecoration: 'underline',
    },
    '&:disabled': {
      color: '#979799',
      cursor: 'default',
      textDecoration: 'none',
    },
  },
  emptyState: {
    padding: '14px 4px 0',
    color: '#979799',
    fontSize: 13,
  },
  emptyStateTitle: {
    color: '#1B1B1F',
    fontSize: 15,
    marginBottom: 4,
  },
}));

export interface GroupedChatListingProps {
  selectedChatSession: SidepanelChat | null;
  onSelectChatSession: (chat: SidepanelChat) => void;
  refetchRef: React.MutableRefObject<() => void>;
  historyActionsRef?: React.MutableRefObject<ChatHistoryActions | null>;
  /**
   * When true (sidebar), this listing scrolls. When false (minimized dropdown), it flows at its
   * natural height and the dropdown scrolls it.
   */
  fillHeight?: boolean;
}

export interface ChatHistoryActions {
  handleStarUpdated: (update: ChatSessionStarUpdate) => Promise<void>;
  refresh: () => Promise<void>;
}

/**
 * Redesigned chat history list (LLM_ENABLE_CHAT_REDESIGN): independently queries and paginates the
 * current user's starred, automated, and recent chats.
 */
interface ChatHistorySectionProps {
  emptyContent?: React.ReactNode;
  history: AccumulatingChatHistory;
  renderItem: (item: AccumulatingChatHistory['items'][number]) => JSX.Element;
  showInitialLoading: boolean;
  testKey: 'starred' | 'automated' | 'recent';
  title: string;
}

function ChatHistorySection({
  emptyContent,
  history,
  renderItem,
  showInitialLoading,
  testKey,
  title,
}: ChatHistorySectionProps): JSX.Element {
  const {classes, cx} = useStyles();
  return (
    <div
      className={classes.section}
      {...getTestProps({component: 'GroupedChatListing', element: `${testKey}-section`})}
    >
      <div
        className={classes.sectionHeader}
        {...getTestProps({component: 'GroupedChatListing', element: `${testKey}-header`})}
      >
        {title}
      </div>
      <div
        aria-busy={history.isRefreshing}
        className={cx(classes.items, history.isRefreshing && classes.refreshing)}
        {...getTestProps({
          component: 'GroupedChatListing',
          element: `${testKey}-items`,
        })}
      >
        {history.isError ? (
          <Container size="sidepanel">
            <InlineBanner
              bannerType={InlineBannerType.ERROR}
              text={`Error loading ${title.toLowerCase()} chats.`}
              action={{
                actionType: InlineBannerActionType.CUSTOM,
                actionText: 'Retry',
                actionOnClick: history.retry,
                rightAlign: true,
              }}
            />
          </Container>
        ) : (
          <>
            {history.items.map((item) => renderItem(item))}
            {history.items.length === 0 && !history.isInitialLoading && emptyContent}
          </>
        )}
        {showInitialLoading && <CenteredHorizontalThrobber />}
        {(history.hasMore || history.isLoadingMore) && (
          <UnstyledButton
            className={classes.viewMore}
            disabled={history.isLoadingMore}
            onClick={() => history.loadMore?.()}
            {...getTestProps({
              component: 'GroupedChatListing',
              element: `${testKey}-view-more`,
            })}
          >
            {history.isLoadingMore ? 'Loading…' : 'View more'}
          </UnstyledButton>
        )}
      </div>
    </div>
  );
}

export default function GroupedChatListing({
  selectedChatSession,
  onSelectChatSession,
  refetchRef,
  historyActionsRef,
  fillHeight = true,
}: GroupedChatListingProps): JSX.Element {
  const {classes, cx} = useStyles();
  const agentsFeatureEnabled = isAgentsFeatureEnabled();
  const starredHistory = useAccumulatingChatHistory({starred: true}, STARRED_CHAT_HISTORY_PAGE_SIZE);
  const automatedHistory = useAccumulatingChatHistory(
    {starred: false, automated: true},
    STARRED_CHAT_HISTORY_PAGE_SIZE,
    {enabled: agentsFeatureEnabled}
  );
  const recentHistory = useAccumulatingChatHistory(
    {starred: false, automated: false},
    CHAT_HISTORY_PAGE_SIZE
  );
  const refreshStarred = starredHistory.refresh;
  const refreshAutomated = automatedHistory.refresh;
  const refreshRecent = recentHistory.refresh;
  const removeAutomatedItem = automatedHistory.removeItem;
  const removeRecentItem = recentHistory.removeItem;
  const removeStarredItem = starredHistory.removeItem;

  const refreshAll = useCallback(async () => {
    await Promise.all([refreshStarred(), refreshAutomated(), refreshRecent()]);
  }, [refreshAutomated, refreshRecent, refreshStarred]);

  const handleStarUpdated = useCallback(
    async ({chatId, isStarred}: ChatSessionStarUpdate) => {
      if (isStarred) {
        removeAutomatedItem(chatId);
        removeRecentItem(chatId);
      } else {
        removeStarredItem(chatId);
      }
      await refreshAll();
    },
    [refreshAll, removeAutomatedItem, removeRecentItem, removeStarredItem]
  );

  useEffect(() => {
    refetchRef.current = refreshAll;
    if (historyActionsRef != null) {
      historyActionsRef.current = {
        handleStarUpdated,
        refresh: refreshAll,
      };
    }
    return () => {
      if (historyActionsRef != null) {
        historyActionsRef.current = null;
      }
    };
  }, [handleStarUpdated, historyActionsRef, refetchRef, refreshAll]);

  const renderItem = useCallback(
    (item: AccumulatingChatHistory['items'][number]): JSX.Element => (
      <RedesignChatRow
        key={item.id ?? 'new-chat'}
        item={item}
        onStarUpdated={handleStarUpdated}
        onSelect={onSelectChatSession}
        isSelected={selectedChatSession?.id === item.id}
      />
    ),
    [handleStarUpdated, onSelectChatSession, selectedChatSession?.id]
  );

  const showStarredSection = starredHistory.items.length > 0 || starredHistory.isError;
  const showAutomatedSection =
    agentsFeatureEnabled && (!automatedHistory.isInitialLoading || automatedHistory.isError);
  const isAnyHistoryInitialLoading =
    starredHistory.isInitialLoading || automatedHistory.isInitialLoading || recentHistory.isInitialLoading;
  const showRecentSection =
    recentHistory.items.length > 0 ||
    recentHistory.isError ||
    recentHistory.isRefreshing ||
    isAnyHistoryInitialLoading;
  const showEmptyState =
    !isAnyHistoryInitialLoading &&
    !starredHistory.isRefreshing &&
    !automatedHistory.isRefreshing &&
    !recentHistory.isRefreshing &&
    !starredHistory.isError &&
    !automatedHistory.isError &&
    !recentHistory.isError &&
    starredHistory.items.length === 0 &&
    automatedHistory.items.length === 0 &&
    recentHistory.items.length === 0;

  return (
    <div
      className={cx(classes.root, fillHeight && 'scrollbar-override', !fillHeight && classes.rootNatural)}
      {...getTestProps({component: 'GroupedChatListing', element: 'root'})}
    >
      {showStarredSection && (
        <ChatHistorySection
          history={starredHistory}
          renderItem={renderItem}
          showInitialLoading={false}
          testKey="starred"
          title="Starred"
        />
      )}
      {showAutomatedSection && (
        <ChatHistorySection
          emptyContent={<SetUpAnAgentRow />}
          history={automatedHistory}
          renderItem={renderItem}
          showInitialLoading={false}
          testKey="automated"
          title="Automated"
        />
      )}
      {showRecentSection && (
        <ChatHistorySection
          history={recentHistory}
          renderItem={renderItem}
          showInitialLoading={isAnyHistoryInitialLoading}
          testKey="recent"
          title="Recent"
        />
      )}
      {showEmptyState && (
        <div
          className={classes.emptyState}
          {...getTestProps({component: 'GroupedChatListing', element: 'empty'})}
        >
          <Text className={classes.emptyStateTitle}>No chats found</Text>
          <Text>Start a new chat from the navigation above.</Text>
        </div>
      )}
    </div>
  );
}
